import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { api, STATUS_STYLES } from "@/lib/api";

const INITIAL_PAGE_GUIDANCE = {
  guidance: {
    page_key: "",
    label: "This page",
    subtitle: "",
    instructions: "Loading page instructions…",
  },
};

const PageGuidanceContext = createContext(INITIAL_PAGE_GUIDANCE);

export function PageGuidanceProvider({ children }) {
  const location = useLocation();
  const [guidance, setGuidance] = useState(INITIAL_PAGE_GUIDANCE.guidance);

  useEffect(() => {
    let active = true;
    setGuidance((current) => ({
      ...current,
      page_key: location.pathname,
      instructions: "Loading page instructions…",
      subtitle: "",
    }));
    api
      .get("/page-guidance", { params: { path: location.pathname } })
      .then(({ data }) => active && setGuidance(data))
      .catch(() => {
        if (active) {
          setGuidance({
            page_key: location.pathname,
            label: "This page",
            subtitle: "",
            instructions: "Page guidance is unavailable right now.",
          });
        }
      });
    return () => {
      active = false;
    };
  }, [location.pathname]);

  const value = useMemo(() => ({ guidance }), [guidance]);
  return (
    <PageGuidanceContext.Provider value={value}>
      {children}
    </PageGuidanceContext.Provider>
  );
}

export function usePageGuidance() {
  return useContext(PageGuidanceContext);
}

export const StatusBadge = ({ status, testId }) => (
  <Badge
    variant="outline"
    className={`font-medium whitespace-nowrap ${STATUS_STYLES[status] || ""}`}
    data-testid={testId || "status-badge"}
  >
    {status}
  </Badge>
);

export const PageHeader = ({
  title,
  subtitle,
  subtitleClassName = "",
  titleClassName = "",
  children,
}) => {
  const { guidance } = usePageGuidance();
  const shownSubtitle = guidance?.subtitle || subtitle;
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
      <div className={titleClassName}>
        <h1 className="text-2xl sm:text-3xl font-bold" data-testid="page-title">
          {title}
        </h1>
        {shownSubtitle && (
          <p
            className={`text-sm text-muted-foreground mt-1 ${subtitleClassName}`}
            data-testid="page-subtitle"
          >
            {shownSubtitle}
          </p>
        )}
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
};

export const Page = ({ children, wide }) => (
  <div className="flex-1 overflow-y-auto">
    <div
      className={`${wide ? "max-w-7xl" : "max-w-5xl"} print-page mx-auto p-4 sm:p-6 lg:p-8`}
    >
      {children}
    </div>
  </div>
);

export const Empty = ({ children }) => (
  <div
    className="border border-dashed rounded-xl p-10 text-center text-sm text-muted-foreground"
    data-testid="empty-state"
  >
    {children}
  </div>
);
